import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { ar, en, fa } from './resources';
import { projectWorkspaceResources } from './projectWorkspaceResources';
import { costingWorkspaceResources } from './costingWorkspaceResources';
import { constructionWorkspaceResources } from './constructionWorkspaceResources';

export const appLocales = ['fa', 'ar', 'en'] as const;

export type AppLocale = (typeof appLocales)[number];

export const localeNativeNames: Readonly<Record<AppLocale, string>> = {
  en: 'English',
  fa: 'فارسی',
  ar: 'العربية',
};

export const localeShortNames: Readonly<Record<AppLocale, string>> = {
  en: 'EN',
  fa: 'فا',
  ar: 'AR',
};

export const defaultLocale: AppLocale = 'fa';

export function isAppLocale(value: string): value is AppLocale {
  return (appLocales as readonly string[]).includes(value);
}

export function isRtlLocale(locale: AppLocale): boolean {
  return locale === 'fa' || locale === 'ar';
}

export function resolveAppLocale(value: string | null | undefined): AppLocale {
  const language = value?.trim().toLowerCase().split(/[-_]/u)[0] ?? '';
  return isAppLocale(language) ? language : defaultLocale;
}

// `app-bootstrap.js` resolves the stored preference before paint; fall back to the product default.
const initialLocale = resolveAppLocale(document.documentElement.lang);

export const i18n = i18next.createInstance();

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: { ...en, projectWorkspace: projectWorkspaceResources.en, costingWorkspace: costingWorkspaceResources.en, constructionWorkspace: constructionWorkspaceResources.en } },
    fa: { translation: { ...fa, projectWorkspace: projectWorkspaceResources.fa, costingWorkspace: costingWorkspaceResources.fa, constructionWorkspace: constructionWorkspaceResources.fa } },
    ar: { translation: { ...ar, projectWorkspace: projectWorkspaceResources.ar, costingWorkspace: costingWorkspaceResources.ar, constructionWorkspace: constructionWorkspaceResources.ar } },
  },
  lng: initialLocale,
  fallbackLng: 'en',
  supportedLngs: appLocales,
  initAsync: false,
  returnNull: false,
  interpolation: {
    escapeValue: false,
  },
});

export function getActiveLocale(): AppLocale {
  return resolveAppLocale(i18n.resolvedLanguage);
}

export async function setActiveLocale(locale: AppLocale): Promise<void> {
  document.documentElement.lang = locale;
  document.documentElement.dir = isRtlLocale(locale) ? 'rtl' : 'ltr';
  document.cookie = `varza.locale=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
  await i18n.changeLanguage(locale);
}
